// Product of Launch Maniac LLC, Las Vegas, Nevada - (725) 444-8200 support@launchmaniac.com

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
// Local type definitions (standalone app -- no shared package)
interface LocationConfig {
  timezone: string;
  forwarding_number: string | null;
  call_recording_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
}

type UserRole = 'ADMIN' | 'OPERATOR' | 'SDR' | 'READ_ONLY';

interface MeResponse {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  location_id: string;
  location_config: LocationConfig | null;
  messaging_ready: boolean;
  setup_complete: boolean;
  first_boot: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('repipe_token'));
  const user = ref<MeResponse | null>(null);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const locationId = computed(() => user.value?.location_id ?? 'default');
  const userName = computed(() => user.value?.name ?? '');
  const userRole = computed(() => user.value?.role as UserRole | undefined);

  async function login(email: string, password: string) {
    loading.value = true;
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        token.value = data.data.token;
        localStorage.setItem('repipe_token', data.data.token);
        await fetchMe();
        return true;
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  function setToken(newToken: string) {
    token.value = newToken;
    localStorage.setItem('repipe_token', newToken);
  }

  async function fetchMe() {
    try {
      const headers: Record<string, string> = {};
      if (token.value) {
        headers['Authorization'] = `Bearer ${token.value}`;
      }
      const res = await fetch(`${API_BASE}/auth/me`, { headers });
      if (!res.ok) {
        if (res.status === 401 && token.value) {
          logout();
        }
        return;
      }
      const data = await res.json();
      if (data.success && data.data) {
        user.value = data.data;
      }
    } catch {
      // Network error -- don't destroy auth state, could be transient
    }
  }

  function logout() {
    token.value = null;
    user.value = null;
    localStorage.removeItem('repipe_token');
  }

  return {
    token,
    user,
    loading,
    isAuthenticated,
    locationId,
    userName,
    userRole,
    setToken,
    login,
    fetchMe,
    logout,
  };
});
